import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const navSections = [
  {
    title: 'Operate',
    items: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Assistant', path: '/assistant' },
      { label: 'Command Centre', path: '/command-centre' },
      { label: 'Storefront', path: '/storefront' },
      { label: 'Checkout', path: '/checkout' },
    ],
  },
  {
    title: 'Finance',
    items: [
      { label: 'Overview', path: '/finance' },
      { label: 'Transactions', path: '/transactions' },
      { label: 'Record Transaction', path: '/record-transaction' },
      { label: 'Accounts', path: '/accounts' },
      { label: 'Invoices', path: '/invoices' },
    ],
  },
  {
    title: 'Build',
    items: [
      { label: 'Catalog', path: '/catalog' },
      { label: 'Forms', path: '/forms' },
      { label: 'Form Analytics', path: '/form-analytics' },
      { label: 'Landing Pages', path: '/pages' },
      { label: 'Media Library', path: '/media-library' },
    ],
  },
  {
    title: 'Engage',
    items: [
      { label: 'Contacts', path: '/contacts' },
      { label: 'Conversations', path: '/conversations' },
      { label: 'Integrations', path: '/integrations' },
    ],
  },
  {
    title: 'Automate',
    items: [
      { label: 'Workflows', path: '/workflows' },
      { label: 'Templates', path: '/templates' },
    ],
  },
  {
    title: 'Manage',
    items: [
      { label: 'Inventory', path: '/inventory' },
      { label: 'Capacity', path: '/capacity' },
      { label: 'Settings', path: '/settings' },
    ],
  },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <aside style={styles.sidebar}>
      <div style={styles.logo}>
        <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'var(--text-primary)' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 204.27 204.27" style={{ width: 24, height: 24 }} aria-label="TechAssembly Logo">
            <g fill="currentColor">
              <path d="M118.81,62.61a23,23,0,0,1-5,3.77,20.78,20.78,0,0,1-11.72,2.56,20.79,20.79,0,0,1-11.73-2.56,23,23,0,0,1-5-3.77L30.82,8.53A29,29,0,0,1,51.37,0,28.59,28.59,0,0,1,69.21,6.79l32.93,32,32.92-32A28.59,28.59,0,0,1,152.9,0a29,29,0,0,1,20.55,8.53Z"/>
              <path d="M118.81,141.66a23,23,0,0,0-5-3.77,21.23,21.23,0,0,0-6.81-2.36,21.58,21.58,0,0,0-4.91-.2,21.65,21.65,0,0,0-4.92.2,21.23,21.23,0,0,0-6.81,2.36,23,23,0,0,0-5,3.77L30.82,195.74a29,29,0,0,0,20.55,8.53,28.54,28.54,0,0,0,17.84-6.79l32.93-32,32.92,32a28.54,28.54,0,0,0,17.84,6.79,29,29,0,0,0,20.55-8.53Z"/>
              <path d="M141.66,85.46a23,23,0,0,0-3.77,5,21.23,21.23,0,0,0-2.36,6.81,21.65,21.65,0,0,0-.2,4.92,21.58,21.58,0,0,0,.2,4.91,21.23,21.23,0,0,0,2.36,6.81,23,23,0,0,0,3.77,5l54.08,54.64a29,29,0,0,0,8.53-20.55,28.54,28.54,0,0,0-6.79-17.84l-32-32.92,32-32.93a28.54,28.54,0,0,0,6.79-17.84,29,29,0,0,0-8.53-20.55Z"/>
              <path d="M62.61,85.46a23,23,0,0,1,3.77,5,20.79,20.79,0,0,1,2.56,11.73,20.78,20.78,0,0,1-2.56,11.72,23,23,0,0,1-3.77,5L8.53,173.45A29,29,0,0,1,0,152.9a28.59,28.59,0,0,1,6.79-17.84l32-32.92-32-32.93A28.59,28.59,0,0,1,0,51.37,29,29,0,0,1,8.53,30.82Z"/>
            </g>
          </svg>
          <span style={styles.logoText}>TechAssembly</span>
        </Link>
      </div>

      <nav style={styles.nav}>
        {navSections.map((section) => (
          <div key={section.title} style={styles.section}>
            <div style={styles.sectionTitle}>{section.title}</div>
            {section.items.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{ ...styles.link, ...(active ? styles.linkActive : {}) }}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={styles.footer}>
        <div style={styles.user}>{user?.email}</div>
        <button onClick={logout} style={styles.logoutBtn}>
          Sign Out
        </button>
      </div>
    </aside>
  );
}

const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: 240,
    height: '100vh',
    background: 'var(--surface-secondary)',
    borderRight: '1px solid var(--border-primary)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: 0,
    top: 0,
    overflowY: 'auto',
    zIndex: 50,
  },
  logo: {
    padding: '16px 16px',
    borderBottom: '1px solid var(--border-primary)',
    display: 'flex',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text-primary)',
    letterSpacing: '-0.025em',
  },
  nav: {
    flex: 1,
    padding: '12px 0',
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    padding: '8px 16px',
    letterSpacing: 2,
  },
  link: {
    display: 'block',
    padding: '8px 16px',
    fontSize: 14,
    color: 'var(--text-secondary)',
    borderRadius: 6,
    margin: '0 8px',
    transition: 'background 150ms',
    textDecoration: 'none',
  },
  linkActive: {
    background: 'var(--accent-subtle)',
    color: 'var(--accent-text)',
    fontWeight: 500,
  },
  footer: {
    padding: '16px',
    borderTop: '1px solid var(--border-primary)',
  },
  user: {
    fontSize: 12,
    color: 'var(--text-tertiary)',
    marginBottom: 8,
    wordBreak: 'break-all',
  },
  logoutBtn: {
    width: '100%',
    padding: '8px 12px',
    fontSize: 13,
    background: 'transparent',
    border: '1px solid var(--border-primary)',
    borderRadius: 6,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
};
